// src/employee/pages/EmployeePayroll.js
import React, { useMemo, useState, useEffect } from "react";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { useEmployeeData } from "../context/EmployeeDataContext";
import { useParams } from "react-router-dom";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { motion, AnimatePresence } from "framer-motion";
import { FaMoneyBillWave, FaDownload, FaEye, FaFilePdf, FaFire, FaChevronDown, FaChevronUp } from "react-icons/fa";
import Papa from "papaparse";
import { format } from "date-fns";

export default function EmployeePayroll() {
  const { user } = useEmployeeAuth();
  const { email } = useParams();
  const targetEmail = email || user.email;
  const { payouts, employee, loading: dataLoading } = useEmployeeData();
  const [targetEmployee, setTargetEmployee] = useState(null);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [expandedPayslip, setExpandedPayslip] = useState(null);
  const [showSalaryBreakdown, setShowSalaryBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!targetEmail) return;
    const q = query(collection(db, "employees"), where("email", "==", targetEmail));
    const unsub = onSnapshot(q, snap => {
      if (!snap.empty) {
        setTargetEmployee({ id: snap.docs[0].id, ...snap.docs[0].data() });
      }
      setLoading(false);
    });
    return () => unsub();
  }, [targetEmail]);

  const activeEmployee = targetEmployee || employee;

  const filteredPayouts = useMemo(() => {
    return payouts.filter(p => p.employeeEmail === targetEmail);
  }, [payouts, targetEmail]);

  const timestampToDate = (ts) => {
    if (!ts) return null;
    return ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  };

  // Add this function inside your component, above generatePDF
  const numberToWords = (num) => {
    if (num === 0) return "Zero";
    const belowTwenty = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const thousands = ["", "Thousand", "Lakh", "Crore"];

    const convert = (n) => {
      if (n < 20) return belowTwenty[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + belowTwenty[n % 10] : "");
      if (n < 1000) return belowTwenty[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convert(n % 100) : "");
      for (let i = 0; i < thousands.length; i++) {
        const unit = Math.pow(1000, i + 1);
        if (n < unit) return convert(Math.floor(n / (unit / 1000))) + " " + thousands[i] + (n % (unit / 1000) ? " " + convert(n % (unit / 1000)) : "");
      }
    };
    return convert(num).trim();
  };

  const stats = useMemo(() => {
    const total = filteredPayouts.reduce((s, p) => s + (p.finalPaid || 0), 0);
    const avg = filteredPayouts.length ? total / filteredPayouts.length : 0;
    const pending = filteredPayouts.filter(p => p.status === "pending").length;
    const bonuses = filteredPayouts.reduce((s, p) => s + (p.bonus || 0), 0);
    return { total, avg: avg.toFixed(0), pending, bonuses };
  }, [filteredPayouts]);

  const exportCSV = () => {
    const rows = filteredPayouts.map(p => ({
      Month: p.month,
      "Gross Salary": p.baseSalary,
      "Total Additions": (p.additions || []).reduce((a, b) => a + b.amount, 0),
      "Total Deductions": (p.deductions || []).reduce((a, b) => a + b.amount, 0),
      "Net Pay": p.finalPaid,
      Status: p.status,
      "Paid On": p.paidOn ? format(timestampToDate(p.paidOn), "dd MMM yyyy") : "",
      Remarks: p.remarks || "",
    }));
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Payslips_${targetEmail}_${format(new Date(), "MMM-yyyy")}.csv`;
    a.click();
  };

  const generatePDF = (p) => {
    const additionsTotal = (p.additions || []).reduce((a, i) => a + i.amount, 0);
    const deductionsTotal = (p.deductions || []).reduce((a, i) => a + i.amount, 0);

    const paidDate = p.paidOn
      ? format(timestampToDate(p.paidOn), "dd MMMM yyyy")
      : "Pending";

    const numberToWords = (num) => {
      if (num === 0) return "Zero";
      const belowTwenty = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
        "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
      const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
      const thousands = ["", "Thousand", "Lakh", "Crore"];

      const convert = (n) => {
        if (n < 20) return belowTwenty[n];
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + belowTwenty[n % 10] : "");
        if (n < 1000) return belowTwenty[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convert(n % 100) : "");
        for (let i = 0; i < thousands.length; i++) {
          const unit = Math.pow(1000, i + 1);
          if (n < unit) return convert(Math.floor(n / (unit / 1000))) + " " + thousands[i] + (n % (unit / 1000) ? " " + convert(n % (unit / 1000)) : "");
        }
      };
      return convert(num).trim();
    };

    const content = `
   <!DOCTYPE html>
   <html>
     <head>
       <meta charset="UTF-8">
       <title>Payslip - ${p.month} - ${activeEmployee?.name || targetEmail}</title>
       <style>
       * { margin: 0; padding: 0; box-sizing: border-box; }
       body { font-family: 'Segoe UI', Tahoma, sans-serif; background: #f8fafc; padding: 30px; color: #1e293b; }
       .container { max-width: 1000px; margin: 0 auto; background: white; border-radius: 20px; overflow: hidden; box-shadow: 0 25px 50px rgba(0,0,0,0.12); }
       .header { background: linear-gradient(135deg, #1e40af, #3b82f6); color: white; padding: 50px 40px; text-align: center; position: relative; }
       .header::before { content: 'DRIVEEASY'; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); font-size: 150px; font-weight: 900; color: rgba(255,255,255,0.08); pointer-events: none; }
       .company { font-size: 42px; font-weight: 900; letter-spacing: 2px; }
       .tagline { font-size: 22px; margin-top: 10px; opacity: 0.95; }

       .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; padding: 40px; background: #f1f5f9; }
       .info-box { background: white; padding: 28px; border-radius: 16px; border: 1px solid #e2e8f0; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
       .info-box h3 { color: #1e40af; font-size: 20px; margin-bottom: 18px; padding-bottom: 10px; border-bottom: 3px solid #3b82f6; font-weight: 700; }
       .info-box p { margin: 14px 0; font-size: 16px; line-height: 1.5; }
       .info-box strong { color: #1e293b; }

       .section { padding: 0 20px 20px; }
       .section-title { font-size: 26px; font-weight: 800; margin: 40px 0 20px; text-align: center; padding: 16px; border-radius: 12px; }
       .gross-pay-title { background: linear-gradient(135deg, #dbeafe, #bfdbfe); color: #1e40af; }
       .deductions-title { background: linear-gradient(135deg, #fee2e2, #fecaca); color: #dc2626; }

       table { width: 100%; border-collapse: collapse; margin: 20px 0; background: white; border-radius: 16px; overflow: hidden; box-shadow: 0 8px 25px rgba(0,0,0,0.1); }
       th { background: #1e40af; color: white; padding: 20px 24px; text-align: left; font-size: 17px; font-weight: 600; }
       th.amount { text-align: right; }
       td { padding: 18px 24px; border-bottom: 1px solid #e2e8f0; font-size: 16px; }
       td.amount { text-align: right; font-weight: 600; font-family: 'Courier New', monospace; }
       .total-row td { background: #1e293b; color: white; font-size: 19px; font-weight: 800; }
       .highlight-row td { background: #f0fdf4; font-weight: bold; color: #059669; }

       .net-pay { background: linear-gradient(135deg, #059669, #10b981); color: white; padding: 60px 40px; text-align: center; }
       .net-label { font-size: 24px; opacity: 0.95; margin-bottom: 12px; }
       .net-amount { font-size: 70px; font-weight: 900; letter-spacing: 3px; margin: 12px 0; }
       .in-words { font-size: 22px; font-style: italic; margin-top: 18px; opacity: 0.95; }

       .footer { text-align: center; padding: 35px; background: #1e293b; color: #94a3b8; font-size: 14px; }
       .footer strong { color: white; }

       @media print { body { padding: 0; background: white; } .container { box-shadow: none; } }
     </style>
   </head>
   <body>
     <div class="container">
       <div class="header">
         <div class="company">DriveEasy</div>
         <div class="tagline">Official Payslip • ${p.month}</div>
       </div>

       <div class="info-grid">
         <div class="info-box">
           <h3>Employee Information</h3>
           <p><strong>Name:</strong> ${activeEmployee?.name || targetEmail}</p>
           <p><strong>Employee ID:</strong> ${activeEmployee?.employeeId || "N/A"}</p>
           <p><strong>Designation:</strong> ${activeEmployee?.designation || "N/A"}</p>
           <p><strong>Department:</strong> ${activeEmployee?.department || "N/A"}</p>
         </div>
         <div class="info-box">
           <h3>Payment Details</h3>
           <p><strong>Pay Period:</strong> ${p.month}</p>
           <p><strong>Payment Date:</strong> ${paidDate}</p>
           <p><strong>Status:</strong> <span style="color:#059669;font-weight:bold;">${p.status.toUpperCase()}</span></p>
           ${p.remarks ? `<p><strong>HR Remarks:</strong> <em style="color:#1e40af;">${p.remarks}</em></p>` : ""}
         </div>
       </div>

       <!-- GROSS PAY (A) -->
       <div class="section">
         <div class="section-title gross-pay-title">
           Gross Pay (A) — Total Earnings Before Deductions
         </div>
         <table>
           <thead>
             <tr>
                   <th style="width:70%;">Earnings</th>
                   <th class="amount">Amount (₹)</th>
                 </tr>
           </thead>
           <tbody>
               <tr>
                 <td>Basic</td>
                 <td class="amount">₹${(activeEmployee?.salaryStructure?.basic || 0).toLocaleString('en-IN')}</td>
               </tr>
               <tr>
                 <td>House Rent Allowance (HRA)</td>
                 <td class="amount">₹${(activeEmployee?.salaryStructure?.hra || 0).toLocaleString('en-IN')}</td>
               </tr>
               <tr>
                 <td>Meal Allowance</td>
                 <td class="amount">₹${(activeEmployee?.salaryStructure?.meal || 0).toLocaleString('en-IN')}</td>
               </tr>
               <tr>
                 <td>City Compensatory Allowance (CCA)</td>
                 <td class="amount">₹${(activeEmployee?.salaryStructure?.cca || 0).toLocaleString('en-IN')}</td>
               </tr>
               <tr>
                 <td>Dearness Allowance (DA)</td>
                 <td class="amount">₹${(activeEmployee?.salaryStructure?.da || 0).toLocaleString('en-IN')}</td>
               </tr>
               <tr>
                 <td>Performance Pay</td>
                 <td class="amount">₹${(activeEmployee?.salaryStructure?.performancePay || 0).toLocaleString('en-IN')}</td>
               </tr>
               <tr>
                 <td>Special Allowance</td>
                 <td class="amount">₹${(activeEmployee?.salaryStructure?.specialAllowance || 0).toLocaleString('en-IN')}</td>
               </tr>

               ${(p.additions || []).filter(a => a.amount > 0).map(a => `
                 <tr class="highlight-row">
                   <td>• ${a.name}</td>
                   <td class="amount">+₹${a.amount.toLocaleString('en-IN')}</td>
                 </tr>
               `).join('')}

               <tr class="total-row">
                 <td><strong>Gross Pay (A)</strong></td>
                 <td class="amount"><strong>₹${(p.baseSalary + additionsTotal).toLocaleString('en-IN')}</strong></td>
               </tr>
             </tbody>
         </table>
       </div>

       <!-- DEDUCTIONS (B) -->
       ${deductionsTotal > 0 ? `
       <div class="section">
         <div class="section-title deductions-title">
           Deductions (B) — Statutory & Other Deductions
         </div>
         <table>
           <thead>
             <tr>
               <th style="width:70%;">Deductions</th>
               <th class="amount">Amount (₹)</th>
             </tr>
           </thead>
           <tbody>
             ${p.deductions.filter(d => d.amount > 0).map(d => `
               <tr>
                 <td>• ${d.name}</td>
                 <td class="amount">-₹${d.amount.toLocaleString('en-IN')}</td>
                 </tr>
             `).join('')}
             <tr class="total-row">
               <td><strong>Total Deductions (B)</strong></td>
               <td class="amount"><strong>-₹${deductionsTotal.toLocaleString('en-IN')}</strong></td>
               </tr>
           </tbody>
         </table>
       </div>
       ` : ''}

       <!-- NET PAY -->
       <div class="net-pay">
         <div class="net-label">Net Salary Credited to Bank Account</div>
         <div class="net-amount">₹${p.finalPaid.toLocaleString('en-IN')}</div>
         <div class="in-words">In Words: <strong>${numberToWords(p.finalPaid)} Rupees Only</strong></div>
       </div>

       <div class="footer">
         This is a system-generated payslip • No signature required<br>
         © 2025 <strong>WheelPro Technologies Pvt. Ltd.</strong> • Generated on ${format(new Date(), "dd MMMM yyyy 'at' hh:mm a")}
       </div>
     </div>
   </body>
   </html>
     `;

    const win = window.open("", "_blank");
    win.document.write(content);
    win.document.close();
    win.onload = () => setTimeout(() => win.print(), 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-purple-950 to-indigo-950 text-white">
      <div className="px-6 pt-6 pb-4 text-center">
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-2xl font-black bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent">
          My Payslips
        </motion.h1>
      </div>

      <div className="max-w-7xl mx-auto px-8 mb-6 flex justify-center">
        <motion.button whileHover={{ scale: 1.05 }} onClick={exportCSV} className="px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl font-bold flex items-center gap-3 shadow-xl text-sm">
          <FaDownload /> Download All as CSV
        </motion.button>
      </div>

      <div className="max-w-7xl mx-auto px-8">
        {(loading || dataLoading) ? (
          <p className="text-center text-white/60 py-20 text-xl">Loading your payslips...</p>
        ) : filteredPayouts.length === 0 ? (
          <div className="text-center py-20">
            <FaMoneyBillWave className="mx-auto text-8xl text-white/20 mb-6" />
            <p className="text-2xl text-white/70">No payslips yet. Your journey has just begun!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPayouts.map((p, i) => {
              const additionsTotal = (p.additions || []).reduce((a, b) => a + b.amount, 0);
              const deductionsTotal = (p.deductions || []).reduce((a, b) => a + b.amount, 0);
              const isExpanded = expandedPayslip === p.id;

              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl overflow-hidden"
                >
                  <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedPayslip(isExpanded ? null : p.id)}>
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-4 items-center">
                      <div>
                        <p className="text-sm text-white/60">Month</p>
                        <p className="text-xl font-bold">{p.month}</p>
                      </div>
                      <div>
                        <p className="text-sm text-white/60">Gross Pay</p>
                        <button onClick={(e) => { e.stopPropagation(); setShowSalaryBreakdown(p); }} className="text-sm font-bold text-cyan-300 hover:underline">
                          ₹{p.baseSalary.toLocaleString()}
                        </button>
                      </div>
                      <div>
                        <p className="text-sm text-white/60">Additions</p>
                        <p className="text-lg font-bold text-emerald-300">+₹{additionsTotal.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-white/60">Deductions</p>
                        <p className="text-lg font-bold text-red-300">-₹{deductionsTotal.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-white/60">Net Pay</p>
                        <p className="text-xl font-black text-white">₹{p.finalPaid.toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 ml-6">
                      <span className={`px-4 py-2 rounded-full text-sm font-bold ${p.status === "paid" ? "bg-emerald-500/30 text-emerald-300" : "bg-orange-500/30 text-orange-300"}`}>
                        {p.status.toUpperCase()}
                      </span>
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                  </div>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        className="border-t border-white/20 bg-gradient-to-b from-white/5 to-transparent"
                      >
                        <div className="p-8">

                          {/* === EARNINGS LEDGER === */}
                          <div className="mb-10">
                            <h3 className="text-2xl font-bold text-emerald-400 mb-6 flex items-center gap-3">
                              <span className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center text-sm">✓</span>
                              Earnings
                            </h3>
                            <div className="bg-black/30 border border-white/10 rounded-2xl overflow-hidden">
                              <table className="w-full">
                                <thead>
                                  <tr className="bg-emerald-900/40">
                                    <th className="px-8 py-5 text-left text-emerald-300 font-semibold">Particulars</th>
                                    <th className="px-8 py-5 text-right text-emerald-300 font-semibold">Amount (₹)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr className="border-t border-white/5">
                                    <td className="px-8 py-5 text-white">Gross Salary</td>
                                    <td className="px-8 py-5 text-right font-bold text-white">₹{p.baseSalary.toLocaleString()}</td>
                                  </tr>
                                  {(p.additions || [])
                                    .filter(a => a.amount > 0)
                                    .map((a, i) => (
                                      <tr key={i} className="border-t border-white/5 hover:bg-white/5 transition">
                                        <td className="px-8 py-5 text-emerald-200">• {a.name}</td>
                                        <td className="px-8 py-5 text-right font-bold text-emerald-300">+₹{a.amount.toLocaleString()}</td>
                                      </tr>
                                    ))}
                                  <tr className="bg-emerald-900/60 border-t-2 border-emerald-400">
                                    <td className="px-8 py-6 text-lg font-bold text-white">Total Earnings</td>
                                    <td className="px-8 py-6 text-right text-xl font-black text-emerald-300">
                                      ₹{(p.baseSalary + additionsTotal).toLocaleString()}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* === DEDUCTIONS LEDGER === */}
                          {(p.deductions || []).length > 0 && (
                            <div className="mb-10">
                              <h3 className="text-2xl font-bold text-red-400 mb-6 flex items-center gap-3">
                                <span className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center text-sm">−</span>
                                Deductions
                              </h3>
                              <div className="bg-black/30 border border-white/10 rounded-2xl overflow-hidden">
                                <table className="w-full">
                                  <thead>
                                    <tr className="bg-red-900/40">
                                      <th className="px-8 py-5 text-left text-red-300 font-semibold">Particulars</th>
                                      <th className="px-8 py-5 text-right text-red-300 font-semibold">Amount (₹)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {p.deductions
                                      .filter(d => d.amount > 0)
                                      .map((d, i) => (
                                        <tr key={i} className="border-t border-white/5 hover:bg-white/5 transition">
                                          <td className="px-8 py-5 text-red-200">• {d.name}</td>
                                          <td className="px-8 py-5 text-right font-bold text-red-300">-₹{d.amount.toLocaleString()}</td>
                                        </tr>
                                      ))}
                                    <tr className="bg-red-900/60 border-t-2 border-red-400">
                                      <td className="px-8 py-6 text-lg font-bold text-white">Total Deductions</td>
                                      <td className="px-8 py-6 text-right text-xl font-black text-red-300">
                                        ₹{deductionsTotal.toLocaleString()}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* === REMARKS === */}
                          {p.remarks && (
                            <div className="mb-8 p-6 bg-gradient-to-r from-amber-900/30 to-orange-900/30 border border-amber-500/30 rounded-2xl">
                              <p className="text-amber-200 font-semibold mb-2">Remarks from HR</p>
                              <p className="text-white text-lg italic">"{p.remarks}"</p>
                            </div>
                          )}

                          {/* === FINAL SUMMARY === */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
                            <div className="text-center p-6 bg-white/5 rounded-2xl border border-white/10">
                              <p className="text-white/70 text-sm">Gross Earnings</p>
                              <p className="text-2xl font-bold text-emerald-300">₹{(p.baseSalary + additionsTotal).toLocaleString()}</p>
                            </div>
                            <div className="text-center p-6 bg-white/5 rounded-2xl border border-white/10">
                              <p className="text-white/70 text-sm">Total Deductions</p>
                              <p className="text-2xl font-bold text-red-300">-₹{deductionsTotal.toLocaleString()}</p>
                            </div>
                            <div className="text-center p-8 bg-gradient-to-r from-cyan-600/40 to-blue-600/40 rounded-2xl border-2 border-cyan-400">
                              <p className="text-cyan-200 text-lg">Net Salary Credited</p>
                              <p className="text-5xl font-black text-white mt-2">₹{p.finalPaid.toLocaleString()}</p>
                            </div>
                          </div>

                          {/* === PDF BUTTON === */}
                          <div className="mt-10 text-center">
                            <button
                              onClick={() => generatePDF(p)}
                              className="px-12 py-5 bg-gradient-to-r from-red-600 to-pink-600 rounded-2xl font-bold text-white text-lg shadow-xl hover:shadow-red-500/50 transform hover:scale-105 transition-all flex items-center gap-4 mx-auto"
                            >
                              <FaFilePdf className="text-2xl" />
                              Download Official Payslip (PDF)
                            </button>
                          </div>

                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Payslip Modal */}
      <AnimatePresence>
        {selectedPayslip && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPayslip(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="bg-white/10 backdrop-blur-3xl border border-white/20 rounded-3xl p-10 w-full max-w-2xl"
              onClick={e => e.stopPropagation()}
            >
              <button onClick={() => setSelectedPayslip(null)} className="float-right text-4xl text-white mb-6">×</button>
              <h2 className="text-4xl font-bold text-white text-center mb-8">
                {selectedPayslip.month} Payslip
              </h2>

              <div className="grid md:grid-cols-2 gap-8 text-white mb-8">
                <div>
                  <p className="text-white/60">Employee</p>
                  <p className="text-2xl font-bold">{activeEmployee?.name || targetEmail}</p>
                </div>
                <div>
                  <p className="text-white/60">Status</p>
                  <p className={`text-3xl font-bold ${selectedPayslip.status === "paid" ? "text-green-400" : "text-orange-400"}`}>
                    {selectedPayslip.status.toUpperCase()}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between p-4 bg-white/5 rounded-2xl">
                  <span className="text-white/80">Base Salary</span>
                  <span className="text-xl font-bold text-white">₹{(selectedPayslip.baseSalary || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between p-4 bg-green-500/10 rounded-2xl">
                  <span className="text-green-300">+ Bonus</span>
                  <span className="text-xl font-bold text-green-300">₹{(selectedPayslip.bonus || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between p-4 bg-red-500/10 rounded-2xl">
                  <span className="text-red-300">- Deductions</span>
                  <span className="text-xl font-bold text-red-300">₹{(selectedPayslip.deduction || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between p-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl text-white text-2xl">
                  <span className="font-bold">Net Pay</span>
                  <span className="font-bold">₹{(selectedPayslip.finalPaid || 0).toLocaleString()}</span>
                </div>
              </div>

              {selectedPayslip.paidOn && (
                <p className="text-center text-white/60 mt-6 text-sm">
                  Paid on {format(timestampToDate(selectedPayslip.paidOn), "dd MMMM yyyy")}
                </p>
              )}

              <div className="mt-8 text-center">
                <button onClick={() => generatePDF(selectedPayslip)}
                  className="px-10 py-4 bg-red-500/20 hover:bg-red-500/40 rounded-3xl font-bold text-red-300 hover:text-white transition flex items-center gap-3 mx-auto">
                  <FaFilePdf /> Download PDF
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Salary Structure Modal — FINAL FIX WITH KEY FORCED RE-RENDER */}
      <AnimatePresence>
        {showSalaryBreakdown && activeEmployee && (
          <motion.div
            key={activeEmployee.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setShowSalaryBreakdown(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white/10 backdrop-blur-3xl border border-white/20 rounded-3xl p-10 w-full max-w-2xl max-h-screen overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  Salary Structure — {showSalaryBreakdown.month}
                </h2>
                <button onClick={() => setShowSalaryBreakdown(null)} className="text-white/60 hover:text-white text-4xl">
                  ×
                </button>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {[
                  { label: "Basic", key: "basic" },
                  { label: "HRA", key: "hra" },
                  { label: "Dearness Allowance (DA)", key: "da" },
                  { label: "CCA", key: "cca" },
                  { label: "Special Allowance", key: "specialAllowance" },
                  { label: "Performance Pay", key: "performancePay" },
                  { label: "Meal Allowance", key: "meal" },
                ].map(({ label, key }) => {
                  const value = activeEmployee.salaryStructure?.[key] ?? 0;
                  return (
                    <div key={key} className="space-y-2">
                      <label className="text-white/80 text-sm block">{label}</label>
                      <div className="px-6 py-5 bg-white/10 border border-white/20 rounded-2xl text-white text-xl font-bold">
                        ₹{Number(value).toLocaleString()}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 p-6 bg-gradient-to-r from-cyan-600/30 to-blue-600/30 rounded-3xl text-center">
                <p className="text-white/80 text-lg mb-2">Gross Monthly Salary</p>
                <p className="text-3xl font-black text-white">
                  ₹{(activeEmployee.salaryStructure?.gross || 0).toLocaleString()}
                </p>
              </div>

              <button
                onClick={() => setShowSalaryBreakdown(null)}
                className="mt-8 w-full py-5 bg-white/10 hover:bg-white/20 rounded-2xl text-white font-bold text-xl"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}